import { Component, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { AcceptanceCriteriaService } from '../../core/services/acceptance-criteria.service';
import { AcceptanceCriteria } from '../../core/models/acceptance-criteria.model';
import { AcceptanceCriteriaFormBaseComponent } from '../acceptance-criteria-form-base.component';

@Component({
  selector: 'app-acceptance-criteria-update',
  templateUrl: './acceptance-criteria-update.component.html',
  styleUrls: ['./acceptance-criteria-update.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
})
export class AcceptanceCriteriaUpdateComponent
  extends AcceptanceCriteriaFormBaseComponent
  implements OnInit
{
  criteriaId!: number;

  // Variables para manejo del nuevo archivo seleccionado (si se sube uno nuevo)
  selectedFile: File | null = null;
  newFileBase64: string | null = null;
  newOriginalFileName: string | null = null;

  constructor(
    protected override fb: FormBuilder,
    private criteriaService: AcceptanceCriteriaService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    super(fb);

    // Asegurarse de que el formulario incluya controles para el archivo
    if (!this.criteriaForm.contains('filePath')) {
      this.criteriaForm.addControl('filePath', this.fb.control(''));
    }
    if (!this.criteriaForm.contains('originalFileName')) {
      this.criteriaForm.addControl('originalFileName', this.fb.control(''));
    }
  }

  ngOnInit(): void {
    this.criteriaId = +this.route.snapshot.paramMap.get('id')!;
    this.criteriaService.getAcceptanceCriteriaById(this.criteriaId).subscribe({
      next: (criteria: AcceptanceCriteria) => {
        this.criteriaForm.patchValue(criteria);
      },
      error: (error) => {
        console.error('Error al cargar el criterio de aceptación:', error.message);
        this.errorMessage =
          error.error.message || 'Ocurrió un error al cargar el criterio de aceptación';
      },
    });
  }

  // Método para capturar y procesar el nuevo archivo seleccionado
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.newOriginalFileName = this.selectedFile.name;

      const reader = new FileReader();
      reader.onload = () => {
        // reader.result devuelve el Data URL (incluye el prefijo "data:...")
        this.newFileBase64 = reader.result as string;
      };
      reader.onerror = (error) => {
        console.error('Error al leer el archivo:', error);
      };
      reader.readAsDataURL(this.selectedFile);
    }
  }

  // Método que retorna el Data URL a partir del valor actual del formulario
  getFileDataUrl(): string {
    const base64 = this.criteriaForm.value.filePath;
    const originalFileName = this.criteriaForm.value.originalFileName;
    if (!base64 || !originalFileName) {
      return '';
    }
    // Si ya tiene el prefijo "data:" se retorna tal cual
    if (base64.startsWith('data:')) {
      return base64;
    }
    // Se determina el MIME type según la extensión del archivo
    const ext = originalFileName.split('.').pop()?.toLowerCase();
    let mime = 'application/octet-stream';
    if (ext === 'png') {
      mime = 'image/png';
    } else if (ext === 'jpg' || ext === 'jpeg') {
      mime = 'image/jpeg';
    } else if (ext === 'gif') {
      mime = 'image/gif';
    }
    return `data:${mime};base64,${base64}`;
  }

  // Método para determinar si el archivo es de tipo imagen (por extensión)
  isImage(filename: string): boolean {
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext === 'png' || ext === 'jpg' || ext === 'jpeg' || ext === 'gif';
  }

  onSubmit(): void {
    if (this.criteriaForm.valid) {
      // Si se ha seleccionado un nuevo archivo, se actualizan los valores del formulario
      if (this.newFileBase64 && this.newOriginalFileName) {
        this.criteriaForm.patchValue({
          filePath: this.newFileBase64,
          originalFileName: this.newOriginalFileName,
        });
      }
      const updatedCriteria: AcceptanceCriteria = {
        ...this.criteriaForm.value,
        id: this.criteriaId,
      };

      this.criteriaService.updateAcceptanceCriteria(updatedCriteria).subscribe({
        next: () => {
          this.router.navigate(['/acceptance-criteria']);
        },
        error: (error) => {
          console.error('Error al actualizar el criterio de aceptación:', error.message);
          this.errorMessage =
            error.error.message || 'Ocurrió un error al actualizar el criterio de aceptación';
        },
        complete: () => {
          console.log('Solicitud de actualización de criterio de aceptación completada');
        },
      });
    } else {
      this.markAllFieldsAsTouched();
    }
  }
}
